/**
 * Dedupe for the `app_utm_captured` event (Oct 2026 hotfix).
 *
 * On web, expo-linking turns every window `message` event into a Linking
 * `url` event that carries the unchanged `window.location.href`. Libraries
 * post window messages constantly (keystrokes, timers), so the root layout's
 * listener re-captured the same attribution params every ~1-2 s on /signup
 * and /login: 6.9% of all PostHog events. Capture a given attribution set
 * once per browser session instead.
 */
const LAST_CAPTURED_KEY = 'mcai_last_utm_captured';

let lastCapturedInMemory: string | null = null;

function sessionStore(): Storage | null {
  try {
    return (globalThis as { sessionStorage?: Storage }).sessionStorage ?? null;
  } catch {
    return null;
  }
}

export function attributionFingerprint(params: Record<string, string | undefined>): string {
  return JSON.stringify(
    Object.keys(params)
      .filter((key) => Boolean(params[key]))
      .sort()
      .map((key) => [key, params[key]]),
  );
}

/**
 * True when `params` is non-empty and differs from the last attribution set
 * captured in this browser session; records it as captured.
 */
export function shouldCaptureAttribution(params: Record<string, string | undefined>): boolean {
  const fingerprint = attributionFingerprint(params);
  if (fingerprint === '[]') return false;
  if (fingerprint === lastCapturedInMemory) return false;
  const store = sessionStore();
  try {
    if (store?.getItem(LAST_CAPTURED_KEY) === fingerprint) {
      lastCapturedInMemory = fingerprint;
      return false;
    }
    store?.setItem(LAST_CAPTURED_KEY, fingerprint);
  } catch {}
  lastCapturedInMemory = fingerprint;
  return true;
}

/** Test-only. */
export function __resetAttributionDedupeForTests(): void {
  lastCapturedInMemory = null;
}
